import React, { useState, useEffect } from 'react';
import { Gift } from 'lucide-react';
import { Link } from 'react-router-dom';
import {
  getProfileRequests,
  acceptProfileInterest,
  denyProfileInterest,
  getProfileDetails,
  getReligions,
  getCastes,
  getMotherTongue,
  getStar,
  getZodiac,
  getCountries,
  getStates,
  getCities,
  getUserSubscriptions,
} from '../utils/api';

export function DashboardContent() {
  const [activeTab, setActiveTab] = useState('new');
  const [requests, setRequests] = useState({ new: [], accepted: [], denied: [] });
  const [loadingRequests, setLoadingRequests] = useState(true);

  const [currentPlan, setCurrentPlan] = useState(null);
  const [loadingPlan, setLoadingPlan] = useState(true);

  // Modal & profile state
  const [showModal, setShowModal] = useState(false);
  const [profileId, setProfileId] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loadingProfile, setLoadingProfile] = useState(false);
  const [profileError, setProfileError] = useState(null);
  const [lightboxImage, setLightboxImage] = useState(null);

  // lookup lists
  const [religions, setReligions] = useState([]);
  const [castes, setCastes] = useState([]);
  const [motherTongues, setMotherTongues] = useState([]);
  const [stars, setStars] = useState([]);
  const [zodiacSigns, setZodiacSigns] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  useEffect(() => {
    async function fetchRequests() {
      setLoadingRequests(true);
      try {
        const res = await getProfileRequests();
        setRequests(res);
      } catch (err) {
        console.error('Error fetching requests:', err);
        alert(err.message || 'Failed to load requests');
      } finally {
        setLoadingRequests(false);
      }
    }
    fetchRequests();
  }, []);

  useEffect(() => {
    async function fetchPlan() {
      setLoadingPlan(true);
      try {
        const subs = await getUserSubscriptions();
        if (subs.length) setCurrentPlan(subs[0]);
      } catch (err) {
        console.error('Error fetching plan:', err);
      } finally {
        setLoadingPlan(false);
      }
    }
    fetchPlan();
  }, []);

  const handleAction = async (action, pid, requester) => {
    try {
      if (action === 'accept') await acceptProfileInterest(pid, requester);
      else await denyProfileInterest(pid, requester);
      const res = await getProfileRequests();
      setRequests(res);
    } catch (err) {
      console.error(err);
      alert(err.message || 'Action failed');
    }
  };

  const openModal = async pid => {
    setProfileId(pid);
    setShowModal(true);
    setLoadingProfile(true);
    setProfileError(null);

    try {
      const data = await getProfileDetails(pid);
      setProfile(data);
      const [rels, cst, mTongues, str, zSigns, couns, sts, cts] =
        await Promise.all([
          getReligions(),
          getCastes(),
          getMotherTongue(),
          getStar(),
          getZodiac(),
          getCountries(),
          getStates(),
          getCities(data.state),
        ]);
      setReligions(rels);
      setCastes(cst);
      setMotherTongues(mTongues);
      setStars(str);
      setZodiacSigns(zSigns);
      setCountries(couns);
      setStates(sts);
      setCities(cts);
    } catch (err) {
      console.error('Error loading profile:', err);
      setProfileError(err.message || 'Failed to load profile');
    } finally {
      setLoadingProfile(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setProfile(null);
    setProfileId(null);
    setProfileError(null);
  };

  const lookup = (list, id) => list.find(item => item._id === id)?.name || '—';

  const renderRequests = list => (
    <div className="space-y-6">
      {list.map(item => (
        <div
          key={item.id}
          className="p-4 flex flex-col md:flex-row md:items-center md:justify-between border-b border-gray-300"
        >
          <div className="flex items-start gap-4">
            <div className="w-36 h-32 rounded-lg overflow-hidden">
              <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">{item.name}</h3>
              <p className="text-sm text-gray-500">Request on: {item.requestTime}</p>
              <button
                onClick={() => openModal(item.profileId)}
                className="mt-2 bg-gradient-to-r from-[#E7603C] via-[#B9309A] to-[#B97639] text-white px-4 py-1 rounded-full"
              >
                View Profile
              </button>
            </div>
          </div>
          {activeTab === 'new' && (
            <div className="flex gap-2 mt-4 md:mt-0">
              <button
                onClick={() => handleAction('accept', item.profileId, item.requesterUser)}
                className="bg-green-600 text-white px-4 py-1 rounded-full"
              >Accept</button>
              <button
                onClick={() => handleAction('deny', item.profileId, item.requesterUser)}
                className="border border-red-500 text-red-500 px-4 py-1 rounded-full"
              >Deny</button>
            </div>
          )}
        </div>
      ))}
    </div>
  );

  return (
    <div className="flex-1 p-8 bg-gray-50">
      <div className="max-w-6xl mx-auto space-y-8">
        {/* My Plan Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">My Plan</h2>
          </div>
          <div className="p-8 text-center">
            {loadingPlan ? (
              <p className="text-gray-500">Loading plan...</p>
            ) : currentPlan ? (
              <div className="mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 via-pink-400 to-purple-500 rounded-2xl flex items-center justify-center">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">{currentPlan.title}</h3>
                <p className="text-gray-500 mb-2">Profile Limit: {currentPlan.profileLimit}</p>
                <p className="text-gray-500 mb-6">
                  Validity:{' '}
                  {typeof currentPlan.remainingDays === 'number'
                    ? (currentPlan.remainingDays > 0
                        ? `${currentPlan.remainingDays} days`
                        : 'Expired')
                    : '—'}
                </p>
                <Link to="/matrimony-pricing">
                  <button className="bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                    Manage Plan
                  </button>
                </Link>
              </div>
            ) : (
              <div className="mb-6">
                <div className="w-20 h-20 mx-auto mb-4 bg-gradient-to-br from-yellow-400 via-pink-400 to-purple-500 rounded-2xl flex items-center justify-center">
                  <Gift className="w-10 h-10 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-800 mb-2">No Plan Active</h3>
                <p className="text-gray-500 mb-6">You haven't subscribed to any plan yet</p>
                <Link to="/matrimony-pricing">
                  <button className="bg-black text-white px-8 py-3 rounded-lg font-medium hover:bg-gray-800 transition-colors">
                    CHOOSE A PLAN
                  </button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Interest Request Section */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-800">Interest Request</h2>
          </div>
          <div className="p-6">
            <div className="flex space-x-4 mb-6 justify-center">
              {['new', 'accepted', 'denied'].map(tab => (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-2 rounded-full text-sm font-medium ${
                    activeTab === tab
                      ? 'bg-green-500 text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab === 'new'
                    ? 'New requests'
                    : tab === 'accepted'
                      ? 'Accepted'
                      : 'Denied'}
                </button>
              ))}
            </div>

            {loadingRequests ? (
              <div className="py-8 text-center">Loading requests...</div>
            ) : activeTab === 'new' && !requests.new.length ? (
              <div className="text-center py-8 text-gray-500">
                <p>No new requests found</p>
              </div>
            ) : activeTab === 'accepted' && !requests.accepted.length ? (
              <div className="text-center py-8 text-gray-500">
                <p>No accepted requests.</p>
              </div>
            ) : activeTab === 'denied' && !requests.denied.length ? (
              <div className="text-center py-8 text-gray-500">
                <p>No denied requests.</p>
              </div>
            ) : (
              renderRequests(requests[activeTab])
            )}
          </div>
        </div>
      </div>

      {/* Profile Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-start justify-center pt-20 z-50"
          onClick={closeModal}
        >
          <div
            className="bg-white rounded-2xl max-w-3xl w-full max-h-[80vh] p-6 overflow-y-auto relative"
            onClick={e => e.stopPropagation()}
          >
            <button onClick={closeModal} className="absolute top-4 right-4 text-xl">×</button>

            {loadingProfile && <div className="py-8 text-center">Loading profile...</div>}
            {profileError && <div className="py-8 text-center text-red-500">{profileError}</div>}

            {profile && (
              <>
                <div className="flex flex-col lg:flex-row gap-8">
                  <img
                    src={profile.images?.[0] || '/card.png'}
                    alt={profile.fullName}
                    className="rounded-2xl w-64 h-80 object-cover"
                  />
                  <div className="flex-1">
                    <h2 className="text-3xl font-bold mb-4">{profile.fullName}</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
                      <div><strong>DOB:</strong> {new Date(profile.dob).toLocaleDateString()}</div>
                      <div><strong>Marital Status:</strong> {profile.maritalStatus}</div>
                      <div><strong>Religion:</strong> {lookup(religions, profile.religion)}</div>
                      <div><strong>Caste:</strong> {lookup(castes, profile.caste)}</div>
                      <div><strong>Mother Tongue:</strong> {lookup(motherTongues, profile.motherTongue)}</div>
                      <div><strong>Star:</strong> {lookup(stars, profile.star)}</div>
                      <div><strong>Zodiac:</strong> {lookup(zodiacSigns, profile.zodiacSign)}</div>
                      <div><strong>Income:</strong> {profile.annualIncome}</div>
                      <div className="col-span-full"><strong>Address:</strong> {profile.address}</div>
                      <div><strong>Country:</strong> {lookup(countries, profile.country)}</div>
                      <div><strong>State:</strong> {lookup(states, profile.state)}</div>
                      <div><strong>City:</strong> {lookup(cities, profile.city)}</div>
                    </div>
                    <div className="mt-8">
                      <h3 className="text-xl font-semibold mb-2">Gallery</h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
                        {profile.images?.map((img, idx) => (
                          <img
                            key={idx}
                            src={img}
                            alt={`Gallery ${idx + 1}`}
                            className="rounded cursor-pointer hover:opacity-90"
                            onClick={() => setLightboxImage(img)}
                          />
                        ))}
                      </div>
                    </div>

                    <div className="mt-8">
                      <h3 className="text-xl font-semibold mb-2">About</h3>
                      <p>{profile.description}</p>
                    </div>
                  </div>
                </div>

                {/* Lightbox Modal */}
                {lightboxImage && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-60"
                    onClick={() => setLightboxImage(null)}
                  >
                    <img src={lightboxImage} alt="Enlarged" className="max-h-[90vh] max-w-[90vw]" />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}